/**
 * Lenis Smooth Scroll — Production-Ready Singleton
 * 
 * Rules:
 *  - Only ONE instance ever lives on window.lenis
 *  - Only ONE RAF/ticker callback registered
 *  - Shopify section reload safe
 *  - No ResizeObserver on document.body (causes constant lenis.resize() spam)
 *  - No lenis.stop() on accordion toggle (causes jerk)
 *  - GSAP ticker used if GSAP is available, else RAF loop
 */

(function () {
  'use strict';

  // ── Guard: reduced motion ──────────────────────────────────────────────────
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // ── Guard: Lenis not loaded ────────────────────────────────────────────────
  if (typeof Lenis === 'undefined') return;

  // ── Guard: already initialized (Shopify section reload safety) ────────────
  if (window.__lenisInitialized) {
    // If a lenis instance already exists, just resize it for the new section
    if (window.lenis) window.lenis.resize();
    return;
  }
  window.__lenisInitialized = true;

  // ── Create Lenis instance ─────────────────────────────────────────────────
  var lenis = new Lenis({
    duration: 1.1,
    easing: function (t) {
      // Ease out cubic — fast start, smooth deceleration
      return 1 - Math.pow(1 - t, 3);
    },
    smoothWheel: true,
    wheelMultiplier: 1.0,
    smoothTouch: false,
    touchMultiplier: 1.5,
    syncTouch: false,
    infinite: false,
  });

  window.lenis = lenis;

  // ── RAF / GSAP ticker ─────────────────────────────────────────────────────
  if (typeof gsap !== 'undefined') {
    // Sync ScrollTrigger with Lenis
    if (typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
    }
    
    // Remove any previously registered lenis ticker to avoid duplicates
    if (window.__lenisTickerCallback) {
      gsap.ticker.remove(window.__lenisTickerCallback);
    }
    window.__lenisTickerCallback = function (time) {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(window.__lenisTickerCallback);
    gsap.ticker.lagSmoothing(500, 33);
  } else {
    // Pure RAF loop — only if GSAP unavailable
    var rafRunning = false;
    function raf(time) {
      lenis.raf(time);
      if (rafRunning) requestAnimationFrame(raf);
    }
    rafRunning = true;
    requestAnimationFrame(raf);

    // Stop RAF loop on destroy
    lenis.on('destroy', function () { rafRunning = false; });
  }

  // ── Anchor link handling ───────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;
    var id = anchor.getAttribute('href');
    if (!id || id === '#') return;
    try {
      var el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -80, duration: 1.1 });
      }
    } catch (err) {
      // Invalid selector — ignore
    }
  });

  // ── Accordion (details) resize — only call resize, NEVER stop/start ───────
  // Stopping lenis mid-scroll causes a visible jerk.
  document.addEventListener('toggle', function (e) {
    if (e.target && e.target.tagName === 'DETAILS') {
      // Wait for CSS transition to finish, then recalculate scroll dimensions
      setTimeout(function () { lenis.resize(); }, 300);
    }
  }, true);

  // ── Shopify Theme Editor section events ───────────────────────────────────
  document.addEventListener('shopify:section:load', function () {
    setTimeout(function () { lenis.resize(); }, 150);
  });
  document.addEventListener('shopify:section:reorder', function () {
    setTimeout(function () { lenis.resize(); }, 150);
  });

  // ── Resize fallback ───────────────────────────────────────────────────────
  // We use a debounced ResizeObserver on body to catch late image/font loads
  // without causing constant resize spam.
  var resizeTimer;
  var ro = new ResizeObserver(function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      if (window.lenis) window.lenis.resize();
    }, 250);
  });
  if (document.body) {
    ro.observe(document.body);
  } else {
    document.addEventListener('DOMContentLoaded', function() {
      ro.observe(document.body);
    });
  }

})();
